import { createApolloClient } from "../../services/apolloClient";
import {
  Order,
  CreateOrderInput,
  OrderStatus,
  OrdersResponse,
  OrderResponse,
  CustomerOrdersResponse,
  CustomerOrderResponse,
  CreateOrderResponse,
} from "./types";
import {
  ORDERS_QUERY,
  ORDER_QUERY,
  CUSTOMER_ORDERS_QUERY,
  CREATE_ORDER_MUTATION,
} from "./mutations";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!error || typeof error !== "object") {
    return fallback;
  }

  const graphQLErrors = (
    error as { graphQLErrors?: Array<{ message?: string }> }
  ).graphQLErrors;

  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    const firstMessage = graphQLErrors[0]?.message;
    if (firstMessage) {
      return firstMessage.replace(/^Error:\s*/i, "");
    }
  }

  if (error instanceof Error && error.message) {
    return error.message.replace(/^Error:\s*/i, "");
  }

  return fallback;
};

const createClient = () => createApolloClient(true);

export const orderService = {
  async getOrders(): Promise<Order[]> {
    try {
      const client = createClient();
      const result = await client.query({
        query: ORDERS_QUERY,
      });

      const data = result.data as OrdersResponse | undefined;

      if (!data?.orders) {
        throw new Error("Failed to fetch orders");
      }

      return data.orders;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to fetch orders"));
    }
  },

  async getOrder(id: string): Promise<Order> {
    try {
      const client = createClient();
      const result = await client.query({
        query: ORDER_QUERY,
        variables: { id },
      });

      const data = result.data as OrderResponse | undefined;

      if (!data?.order) {
        throw new Error("Failed to fetch order");
      }

      return data.order;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to fetch order"));
    }
  },

  async getCustomerOrders(status: OrderStatus): Promise<Order[]> {
    try {
      const client = createClient();
      const result = await client.query({
        query: CUSTOMER_ORDERS_QUERY,
        variables: { status },
      });

      const data = result.data as
        | CustomerOrdersResponse
        | CustomerOrderResponse
        | undefined;
      const orders =
        (data as CustomerOrdersResponse | undefined)?.customerOrders ??
        (data as CustomerOrderResponse | undefined)?.data?.customerOrders;

      if (!orders) {
        throw new Error("Failed to fetch customer orders");
      }

      return orders;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, "Failed to fetch customer orders"),
      );
    }
  },

  async createOrder(input: CreateOrderInput): Promise<Order> {
    try {
      const client = createClient();
      const result = await client.mutate({
        mutation: CREATE_ORDER_MUTATION,
        variables: input,
      });

      const data = result.data as CreateOrderResponse | undefined;

      if (!data?.createOrder) {
        throw new Error("Failed to create order");
      }

      return data.createOrder;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to create order"));
    }
  },
};
